'use client';
import classNames from 'classnames';
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react';
import { useDeepCompareEffect } from 'react-use';
import SignaturePad, { FromDataOptions, Options, PointGroup } from 'signature_pad';
import trimCanvas from 'trim-canvas';

import { TrashIcon } from '@/components';

export interface SignatureCanvasProps extends Options {
  canvasProps?: React.CanvasHTMLAttributes<HTMLCanvasElement>;
  clearOnResize?: boolean;
  extra?: ReactNode;
  className?: string;
  onBeginStroke?: () => void;
  onEndStroke?: () => void;
}

export interface SignatureCanvasRef {
  getTrimmedCanvas: () => HTMLCanvasElement;
  getSignaturePad: () => SignaturePad;
  clear: () => void;
  on: () => void;
  off: () => void;
  fromDataURL: (
    dataUrl: string,
    options?: {
      ratio?: number | undefined;
      width?: number | undefined;
      height?: number | undefined;
      callback?: ((error?: ErrorEvent) => void) | undefined;
    }
  ) => void;
  /**
   *   Returns signature image as data URL.
   *   If 'type' parameter is ommitted, PNG dataUrl is returned.
   */
  toDataURL: (type?: string, encoderOptions?: any) => string;
  isEmpty: () => boolean;
  fromData(pointGroups: PointGroup[], { clear }?: FromDataOptions): void;
  toData(): PointGroup[];
}
const SignatureCanvas: ForwardRefRenderFunction<SignatureCanvasRef, SignatureCanvasProps> = (
  { canvasProps, clearOnResize, extra, className, onBeginStroke, onEndStroke, ...sigPadOptions }: SignatureCanvasProps,
  ref
) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sigPadRef = useRef<SignaturePad | null>(null);

  // 初始化 SignaturePad 和事件监听器
  useDeepCompareEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const signaturePad = new SignaturePad(canvas!, sigPadOptions as any);

    signaturePad.addEventListener('beginStroke', () => {
      onBeginStroke?.();
    });
    signaturePad.addEventListener('endStroke', () => {
      onEndStroke?.();
    });

    sigPadRef.current = signaturePad;

    const resizeCanvas = () => {
      const { width, height } = canvasProps ?? {};
      if (typeof width !== 'undefined' && typeof height !== 'undefined') {
        return;
      }

      const ratio = Math.max(window.devicePixelRatio ?? 1, 1);

      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      console.log('resizeCanvas', canvas.offsetHeight, ratio, canvas.height);
      canvas.getContext('2d')?.scale(ratio, ratio);
      sigPadRef.current?.clear();
    };

    const checkClearOnResize = () => {
      if (clearOnResize) {
        resizeCanvas();
      }
    };

    window.addEventListener('resize', checkClearOnResize);
    resizeCanvas();

    return () => {
      window.removeEventListener('resize', checkClearOnResize);
    };
  }, [canvasProps, clearOnResize, sigPadOptions]);

  // 更新 SignaturePad 的属性
  useEffect(() => {
    if (sigPadRef.current) {
      Object.assign(sigPadRef.current, sigPadOptions);
    }
  }, [sigPadOptions]);

  const getSignaturePad = useCallback(() => {
    if (!sigPadRef.current) {
      throw new Error(
        'react-signature-canvas is currently ' + 'mounting or unmounting: React refs are null during this phase.'
      );
    }
    return sigPadRef.current;
  }, []);

  const getCanvas = useCallback(() => {
    if (!canvasRef.current) {
      throw new Error(
        'react-signature-canvas is currently ' + 'mounting or unmounting: React refs are null during this phase.'
      );
    }
    return canvasRef.current;
  }, []);

  const getTrimmedCanvas = useCallback(() => {
    const canvas = getCanvas();
    const copy = document.createElement('canvas');
    copy.width = canvas.width;
    copy.height = canvas.height;
    copy?.getContext('2d')?.drawImage(canvas, 0, 0);
    return trimCanvas(copy);
  }, [getCanvas]);

  useImperativeHandle(ref, () => ({
    getCanvas,
    getTrimmedCanvas,
    getSignaturePad,
    on: () => getSignaturePad().on(),
    off: () => getSignaturePad().off(),
    clear: () => getSignaturePad().clear(),
    isEmpty: () => getSignaturePad().isEmpty(),
    fromDataURL: (...arg) => getSignaturePad().fromDataURL(...arg),
    toDataURL: (...arg) => getSignaturePad().toDataURL(...arg),
    fromData: (...arg) => getSignaturePad().fromData(...arg),
    toData: () => getSignaturePad().toData()
  }));

  return (
    <div className={classNames('border-gray-200 border rounded-[8px] py-[16px] px-[18px] relative', className)}>
      <div className="text-tangerine-500 text-sm font-medium text-center">EthSigned By: </div>
      <div
        className="absolute right-[18px] top-[16px] cursor-pointer"
        onClick={() => {
          sigPadRef.current?.clear();
        }}
      >
        <TrashIcon stroke={'#98A2B3'} />
      </div>
      <canvas ref={canvasRef} {...canvasProps} />
      {extra}
    </div>
  );
};

export default forwardRef(SignatureCanvas);
